import { yupResolver } from '@hookform/resolvers/yup';
import { DateType } from 'components/Inputs/DateRange/Datepicker';
import {
  DATE_RANGE_DEFAULT,
  INVOICE_ID_DEFAULT,
  LANGUAGES_DEFAULT,
  PAGE_DEFAULT,
  PO_CODE_DEFAULT,
  QUALITY_DEFAULT,
  QUERY_DEFAULT,
  SORT_ATTRIBUTE_TRANSACTIONS_DEFAULT,
  SORT_ORDER_DEFAULT,
  TRANSACTION_TYPE_DEFAULT,
  USER_ID_DEFAULT
} from 'constants/defaults';
import {
  InputMaybe,
  QualityEnum,
  QueryTransactionsArgs,
  QueryTransactionStatsArgs,
  SortOrderEnum,
  TransactionPage,
  TransactionsDownloadFilterInput,
  TransactionSortAttributeEnum,
  TransactionStats,
  TransactionTypeEnum
} from 'gql/graphql';
import _ from 'lodash';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTransactions } from 'providers/TransactionsProviders/useTransactions';
import { useTransactionStats } from 'providers/TransactionsProviders/useTransactionStats';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { Control, useForm, useWatch } from 'react-hook-form';
import { KeysEnum } from 'types/KeysEnum';
import { TScheme } from 'types/TScheme';
import { useDebounce } from 'use-debounce';
import { extractParam } from 'utils/paramsUtil';
import { endDate } from 'utils/time/endDate';
import * as yup from 'yup';

export interface FiltersTransactionForm
  extends Omit<QueryTransactionStatsArgs, 'page' | 'perPage'> {
  languages: { sourceLanguageCode: string; targetLanguageCode: string };
  dateRange: {
    startDate: DateType;
    endDate: DateType;
  };
  transactionType?: TransactionTypeEnum;
}

interface IUseSearchParamsTransactionsOut {
  transactionStats?: Array<TransactionStats>;
  transactions?: TransactionPage;
  isLoading: boolean;
  isPending: boolean;
  isRetrieving: boolean;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  totalPages: number;
  handleClear: () => void;
  control: Control<FiltersTransactionForm>;
  filtersAdded: boolean;
  filters: TransactionsDownloadFilterInput;
  sortAttribute?: InputMaybe<TransactionSortAttributeEnum>;
  sortOrder?: InputMaybe<SortOrderEnum>;
}

export default function useSearchParamsTransactions(): IUseSearchParamsTransactionsOut {
  const schema = yup.object().shape({} as TScheme<FiltersTransactionForm>);

  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [page, setPage] = useState(PAGE_DEFAULT);

  const [sortAttribute, setSortAttribute] = useState<
    InputMaybe<TransactionSortAttributeEnum> | undefined
  >(SORT_ATTRIBUTE_TRANSACTIONS_DEFAULT);
  const [sortOrder, setSortOrder] = useState<InputMaybe<SortOrderEnum> | undefined>(
    SORT_ORDER_DEFAULT
  );

  const {
    control,
    setValue,
    formState: { isDirty }
  } = useForm<FiltersTransactionForm>({
    resolver: yupResolver(schema)
  });

  const pageInitial = useMemo(
    () => extractParam(searchParams, KeysEnum.Page, PAGE_DEFAULT) as number,
    [searchParams]
  );

  const queryInitial = searchParams.get(KeysEnum.Query) ?? QUERY_DEFAULT;

  const languagesInitial = useMemo(
    () => extractParam(searchParams, KeysEnum.Languages, LANGUAGES_DEFAULT),
    [searchParams]
  );

  const dateRangeInitial = useMemo(
    () => extractParam(searchParams, KeysEnum.DateRange, DATE_RANGE_DEFAULT),
    [searchParams]
  );

  const qualityInitial = useMemo(
    () => (searchParams.get(KeysEnum.Quality) ?? QUALITY_DEFAULT) as QualityEnum,
    [searchParams]
  );

  const transactionTypeInitial = useMemo(
    () =>
      (searchParams.get(KeysEnum.TransactionType) ??
        TRANSACTION_TYPE_DEFAULT) as TransactionTypeEnum,
    [searchParams]
  );

  const userIdInitial = useMemo(
    () => searchParams.get(KeysEnum.UserId) ?? USER_ID_DEFAULT,
    [searchParams]
  );

  const invoiceIdInitial = useMemo(
    () => searchParams.get(KeysEnum.InvoiceId) ?? INVOICE_ID_DEFAULT,
    [searchParams]
  );

  const poCodeInitial = useMemo(
    () => searchParams.get(KeysEnum.PoCode) ?? PO_CODE_DEFAULT,
    [searchParams]
  );

  const sortAttributeInitial = extractParam(
    searchParams,
    KeysEnum.SortAttribute,
    SORT_ATTRIBUTE_TRANSACTIONS_DEFAULT
  );

  const sortOrderInitial = extractParam(searchParams, KeysEnum.SortOrder, SORT_ORDER_DEFAULT);

  const query = useWatch<FiltersTransactionForm>({
    control,
    name: KeysEnum.Query,
    defaultValue: QUERY_DEFAULT
  });

  const [deferredQuery] = useDebounce(query, 1000);

  const languages = useWatch<FiltersTransactionForm>({
    control,
    name: KeysEnum.Languages,
    defaultValue: LANGUAGES_DEFAULT
  });

  const dateRange = useWatch<FiltersTransactionForm>({
    control,
    name: KeysEnum.DateRange,
    defaultValue: DATE_RANGE_DEFAULT
  });

  const quality = useWatch<FiltersTransactionForm>({
    control,
    name: KeysEnum.Quality,
    defaultValue: QUALITY_DEFAULT
  });

  const transactionType = useWatch<FiltersTransactionForm>({
    control,
    name: KeysEnum.TransactionType,
    defaultValue: TRANSACTION_TYPE_DEFAULT
  });

  const userId = useWatch<FiltersTransactionForm>({
    control,
    name: KeysEnum.UserId,
    defaultValue: USER_ID_DEFAULT
  });

  const invoiceId = useWatch<FiltersTransactionForm>({
    control,
    name: KeysEnum.InvoiceId,
    defaultValue: INVOICE_ID_DEFAULT
  });

  const poCode = useWatch<FiltersTransactionForm>({
    control,
    name: KeysEnum.PoCode,
    defaultValue: PO_CODE_DEFAULT
  });

  const filtersAdded = useMemo(
    () =>
      [
        page !== PAGE_DEFAULT,
        query !== QUERY_DEFAULT,
        languages?.sourceLanguageCode !== LANGUAGES_DEFAULT.sourceLanguageCode,
        languages?.targetLanguageCode !== LANGUAGES_DEFAULT.targetLanguageCode,
        dateRange?.startDate !== DATE_RANGE_DEFAULT.startDate,
        dateRange?.endDate !== DATE_RANGE_DEFAULT.startDate,
        quality !== QUALITY_DEFAULT,
        transactionType !== TRANSACTION_TYPE_DEFAULT,
        !!invoiceId,
        !!poCode,
        !!userId
      ].some(Boolean),
    [
      page,
      query,
      languages?.sourceLanguageCode,
      languages?.targetLanguageCode,
      dateRange?.startDate,
      dateRange?.endDate,
      quality,
      transactionType,
      invoiceId,
      poCode,
      userId
    ]
  );

  const filters: TransactionsDownloadFilterInput = {
    query: deferredQuery ? deferredQuery : undefined,
    sourceLanguageCode: languages?.sourceLanguageCode,
    targetLanguageCode: languages?.targetLanguageCode,
    createdAtAfter: dateRange?.startDate ? new Date(dateRange?.startDate).toISOString() : undefined,
    createdAtBefore: dateRange?.endDate ? endDate(dateRange?.endDate) : undefined,
    quality,
    transactionType,
    userId,
    invoiceId
  };

  const input: QueryTransactionsArgs = {
    ...filters,
    sortAttribute,
    sortOrder,
    page
  };

  const { data: transactionStats } = useTransactionStats(input);
  const { data: transactions, isLoading, isPending } = useTransactions(input);

  const totalPages = transactions?.pagesCount ?? 0;

  const isRetrieving = isLoading && page === 1;

  const resetFilters = useCallback(() => {
    setPage(pageInitial);
    setValue(KeysEnum.Query, queryInitial);
    setValue(KeysEnum.Languages, languagesInitial);
    setValue(KeysEnum.DateRange, dateRangeInitial);
    setValue(KeysEnum.Quality, qualityInitial);
    setValue(KeysEnum.TransactionType, transactionTypeInitial);
    setValue(KeysEnum.UserId, userIdInitial);
    setValue(KeysEnum.InvoiceId, invoiceIdInitial);
    setValue(KeysEnum.PoCode, poCodeInitial);
    setSortAttribute(sortAttributeInitial);
    setSortOrder(sortOrderInitial);
  }, [
    dateRangeInitial,
    invoiceIdInitial,
    languagesInitial,
    pageInitial,
    poCodeInitial,
    qualityInitial,
    queryInitial,
    setValue,
    sortAttributeInitial,
    sortOrderInitial,
    transactionTypeInitial,
    userIdInitial
  ]);

  const handleClear = useCallback(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);

      url.searchParams.delete(KeysEnum.Page);
      url.searchParams.delete(KeysEnum.Query);
      url.searchParams.delete(KeysEnum.Languages);
      url.searchParams.delete(KeysEnum.DateRange);
      url.searchParams.delete(KeysEnum.Quality);
      url.searchParams.delete(KeysEnum.TransactionType);
      url.searchParams.delete(KeysEnum.UserId);
      url.searchParams.delete(KeysEnum.InvoiceId);
      url.searchParams.delete(KeysEnum.PoCode);
      url.searchParams.delete(KeysEnum.SortAttribute);
      url.searchParams.delete(KeysEnum.SortOrder);

      resetFilters();

      window.history.replaceState({}, '', url.toString());
    }
  }, [resetFilters]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);

      if (deferredQuery) url.searchParams.set(KeysEnum.Query, JSON.stringify(deferredQuery));
      else url.searchParams.delete(KeysEnum.Query);

      if (
        languages?.sourceLanguageCode !== undefined &&
        languages?.targetLanguageCode !== undefined
      )
        url.searchParams.set(KeysEnum.Languages, JSON.stringify(languages));
      else url.searchParams.delete(KeysEnum.Languages);

      if (
        dateRange?.startDate !== dateRangeInitial?.startDate &&
        dateRange?.endDate !== dateRangeInitial?.endDate
      )
        url.searchParams.set(KeysEnum.DateRange, JSON.stringify(dateRange));
      else url.searchParams.delete(KeysEnum.DateRange);

      if (quality) url.searchParams.set(KeysEnum.Quality, quality);
      else url.searchParams.delete(KeysEnum.Quality);

      if (transactionType) url.searchParams.set(KeysEnum.TransactionType, transactionType);
      else url.searchParams.delete(KeysEnum.TransactionType);

      if (userId) url.searchParams.set(KeysEnum.UserId, userId);
      else url.searchParams.delete(KeysEnum.UserId);

      if (invoiceId) url.searchParams.set(KeysEnum.InvoiceId, invoiceId);
      else url.searchParams.delete(KeysEnum.InvoiceId);

      if (poCode) url.searchParams.set(KeysEnum.PoCode, poCode);
      else url.searchParams.delete(KeysEnum.PoCode);

      if (sortOrder && sortAttribute)
        url.searchParams.set(KeysEnum.SortOrder, JSON.stringify(sortOrder));
      else url.searchParams.delete(KeysEnum.SortOrder);

      if (sortAttribute && sortOrder)
        url.searchParams.set(KeysEnum.SortAttribute, JSON.stringify(sortAttribute));
      else url.searchParams.delete(KeysEnum.SortAttribute);

      url.searchParams.delete(KeysEnum.Page);
      setPage(
        [
          queryInitial !== deferredQuery,
          !_.isEqual(languagesInitial, languages),
          !_.isEqual(dateRangeInitial, dateRange),
          qualityInitial !== quality,
          transactionTypeInitial !== transactionType,
          userIdInitial !== userId,
          invoiceIdInitial !== invoiceId,
          poCodeInitial !== poCode,
          sortOrder && sortAttribute
        ].some(Boolean) && isDirty
          ? PAGE_DEFAULT
          : pageInitial
      );

      window.history.replaceState({}, '', url.toString());
    }
  }, [
    pageInitial,
    queryInitial,
    deferredQuery,
    sortOrder,
    sortAttribute,
    languages,
    dateRange,
    dateRangeInitial,
    quality,
    transactionType,
    userId,
    invoiceId,
    poCode,
    languagesInitial,
    qualityInitial,
    transactionTypeInitial,
    userIdInitial,
    invoiceIdInitial,
    poCodeInitial,
    isDirty
  ]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);

      if (page > PAGE_DEFAULT) url.searchParams.set(KeysEnum.Page, JSON.stringify(page));
      else url.searchParams.delete(KeysEnum.Page);

      window.history.replaceState({}, '', url.toString());
    }
  }, [page, pathname]);

  useEffect(() => {
    resetFilters();
  }, [resetFilters]);

  return {
    transactionStats,
    transactions,
    isLoading,
    isPending,
    isRetrieving,
    page,
    setPage,
    totalPages,
    handleClear,
    control,
    filtersAdded,
    filters,
    sortAttribute,
    sortOrder
  };
}
